import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import Banner from '../banner/Banner.jsx';
import Button from '../button/Button.jsx';
import SelectFieldEditor from '../select-field-editor/SelectFieldEditor.jsx';
import Spacer from '../spacer/Spacer.jsx';
import Spinner from '../spinner/Spinner.jsx';
import TextFieldEditor from '../text-field-editor/TextFieldEditor.jsx';

import s from './SpritesheetInput.module.scss';

const mapIndexed = R.addIndex(R.map);

const errorWithGettingContent = errors => {
    const { contentErrors } = errors;

    const createRow = ({ canonicalName, contentType, category }, i) => (
        <tr key={i}>
            <td>{canonicalName}</td>
            <td>{contentType}</td>
            <td>{category}</td>
            <td>{`${canonicalName} svg is missing from the ${category}/${contentType} directory or subdirectories`}</td>
        </tr>
    );

    return (
        <table className={s.errorTable}>
            <thead>
                <tr>
                    <th>Canonical Name</th>
                    <th>Content Type</th>
                    <th>Category</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>{mapIndexed(createRow, contentErrors)}</tbody>
        </table>
    );
};

const errorWithMultipleSvgs = errors => {
    const { nameErrors } = errors;

    const createRow = ({ canonicalName, category, listNames }, i) => {
        const description = R.includes('badge', listNames)
            ? `${canonicalName} svg is in the theme and potentially in the badge directory, along with ${R.join(', ', R.without(['badge'], listNames))}`
            : `${canonicalName} svg is in multiple directories ${R.join(', ', listNames)}`;

        return (
            <tr key={i}>
                <td>{canonicalName}</td>
                <td>{category}</td>
                <td>{description}</td>
            </tr>
        );
    };

    return (
        <table className={s.errorTable}>
            <thead>
                <tr>
                    <th>Canonical Name</th>
                    <th>Category</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>{mapIndexed(createRow, nameErrors)}</tbody>
        </table>
    );
};

const renderErrorsTable = errors => {
    return (
        <React.Fragment>
            <h3>Missing svg errors</h3>
            {errorWithGettingContent(errors)}
            <hr />
            <h3>Too many svg errors</h3>
            {errorWithMultipleSvgs(errors)}
        </React.Fragment>
    );
};

const specificityInput = (label, onChange, value) => (
    <React.Fragment key={label}>
        <TextFieldEditor className={s.specificityInput} label={label} value={value} onChange={onChange} />
        <Spacer height={15} />
    </React.Fragment>
);

const dropDownInput = (label, onChange, options, selectedValue) => (
    <React.Fragment>
        <SelectFieldEditor label={label} selectedValue={selectedValue} options={options} onValueSelected={onChange} />
        <Spacer height={15} />
    </React.Fragment>
);

const renderSpinner = (loading, label) => (loading ? <Spinner loading={loading} small /> : label);

const verticals = [
    { value: 'airport', label: 'airport' },
    { value: 'casino', label: 'casino' },
    { value: 'smartcampus', label: 'smartcampus' },
];

const SpritesheetInput = props => {
    const { venueCategory, venueId, accountId, onCategoryChange, onVenueChange, onAccountChange, generateSpritesheet, errors, loading, banner, venueDropdown } = props;

    return (
        <div className={s.cmdTool}>
            <div>Generate Spritesheets</div>
            <Banner banner={banner} />
            <div className={s.spritesheetContainer}>
                {dropDownInput('Venue Category', onCategoryChange, verticals, venueCategory)}

                {dropDownInput('VenueId', onVenueChange, venueDropdown, venueId)}

                {specificityInput('AccountId', onAccountChange, accountId)}

                <Button disabled={loading} label={renderSpinner(loading, 'Generate Spritesheet')} onClick={generateSpritesheet} />
            </div>

            {!R.isEmpty(errors.nameErrors) || !R.isEmpty(errors.contentErrors) ? renderErrorsTable(errors) : null}
        </div>
    );
};

SpritesheetInput.propTypes = {
    venueCategory: PropTypes.string.isRequired,
    venueId: PropTypes.string.isRequired,
    accountId: PropTypes.string.isRequired,
    onCategoryChange: PropTypes.func.isRequired,
    onVenueChange: PropTypes.func.isRequired,
    onAccountChange: PropTypes.func.isRequired,
    generateSpritesheet: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    banner: PropTypes.object.isRequired,
    venueDropdown: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default SpritesheetInput;
